import {Component} from '@angular/core'
import {Router, ActivatedRoute} from '@angular/router'
import { NuclearObservable } from '../../nuclear-observable'
import { BaseComponent } from '../../components/base.component'
import {Customer, Keyword} from '../../types'
import {customerGetters} from '../../stores/customer-store'
import { KeywordActions } from '../../actions/keyword-actions'
import { CustomerActions } from '../../actions/customer-actions'
import {
  CreateKeywordInput,
  DeleteKeywordInput,
  UpdateKeywordInput
} from '../../graphql/inputs/keyword.inputs'

@Component({
  selector: 'nc-topics-page-v1',
  styles: [`
    .keyword-panel-container {
      max-width: 500px;
    }
  `],
  template: `
    <split-view>
      <div left>
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-12">
              <div class="nc-section-icon fas fa-edit"></div>
              <h3 class="nc-section-header">
                All content starts with a request.
              </h3>

              <p class="top-buffer">
                <span class="bold">Title: </span>
                <span>When you submit a request, a writer will start a blog post on your topic. It's that simple.</span>
              </p>
              <p class="top-buffer">
                <span class="bold">Description: </span>
                <span>Use the "Description" section to provide more context around a topic. Include a web link. Write a short outline. The more information you can provide, the better blog posts you'll get.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div right>
        <div class="container-fluid big-top-buffer">
          <div class="row">
            <div class="col-sm-12">
              <h3>Create your first request</h3>
            </div>
          </div>
          <div class="row top-buffer bottom-buffer" *ngIf="keywords?.length > 0">
            <div class="col-sm-12 keyword-panel-container">
              <p>Added requests:</p>
              <ng-template [ngIf]="customer">
                <keyword-panel
                  *ngFor="let keyword of keywords"
                  [keyword]="keyword"
                  [newCustomer]="true"
                  [customer]="customer"
                  (update)="updateKeyword($event)"
                  (delete)="deleteKeyword($event)"
                  (toggleExpanded)="onToggleExpanded($event, keyword)"
                  (toggleEditing)="onToggleEditing($event, keyword)"
                  ></keyword-panel>
              </ng-template>
            </div>
          </div>
          <div class="row top-buffer bottom-buffer">
            <div class="col-sm-12">
              <ng-template [ngIf]="keywords?.length > 0">
                <p>Feel free to add more than one:</p>
                <button type="button" class="btn btn-default" *ngIf="!newKeywordOpen" (click)="newKeywordOpen = true">Add another</button>
              </ng-template>
              <div class="keyword-panel-container">
                <keyword-panel
                  *ngIf="customer && shouldShowNewKeyword()"
                  [newCustomer]="true"
                  [open]="true"
                  [customer]="customer"
                  [newState]="'active'"
                  (close)="newKeywordOpen = false"
                  (create)="createKeyword($event)"
                  ></keyword-panel>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="text-right float-right top-buffer bottom-buffer">
                <button (click)="updateCustomer()" class="block btn btn-primary mr-2" *ngIf="keywords?.length > 0">Continue to plan</button>
                <br>
                <button (click)="navigateToPlanPage()" class="block btn btn-default" *ngIf="!keywords || keywords?.length == 0">Skip for now</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </split-view>
  `
})
export class NcTopicsPageV1 extends BaseComponent {
  customer: Customer;
  keywords: Keyword[] = [];
  private newKeywordOpen: boolean = true;
  private keywordsLoaded: boolean = false;
  private customerId: number;

  constructor(
    private route: ActivatedRoute,
    private keywordActions: KeywordActions,
    private Router: Router,
    CustomerActions: CustomerActions) {
    super()

    CustomerActions.loadCustomers()
    const routeObs = this.route.params.subscribe(params => {
      this.clearRouteObservers()
      this.customerId = +params['customerId']

      this.keywordActions.loadKeywords(this.customerId).then(() => {
        this.keywordsLoaded = true
      })

      const custObs = NuclearObservable.from<Customer>(customerGetters.customer(this.customerId), { toJS: true }).subscribe(c => this.customer = c)
      const kwObs = NuclearObservable.from<Keyword[]>(customerGetters.keywords(this.customerId), { toJS: true }).subscribe(ks => {
        this.keywords = ks
        this.newKeywordOpen = this.keywords.length === 0
        this.keywordsLoaded = true
      })
      this.routeObservers.push(custObs, kwObs)
    })
    this.observers.push(routeObs)
  }

  shouldShowNewKeyword(): boolean {
    return this.keywordsLoaded && (this.newKeywordOpen || this.keywords.length === 0)
  }

  updateCustomer(): void {
    this.navigateToPlanPage()
  }

  navigateToPlanPage(params: Object = null): void {
    this.Router.navigate(['../', 'plan'], { relativeTo: this.route, queryParams: params })
  }

  createKeyword(input: CreateKeywordInput): void {
    this.keywordActions.createKeyword(input.customerId, input.body)
  }

  updateKeyword({ customerId, id, updates }: UpdateKeywordInput): void {
    this.keywordActions.updateKeyword(customerId, id, updates)
  }

  deleteKeyword(input: DeleteKeywordInput): void {
    this.keywordActions.deleteKeyword(input.customerId, input.id)
  }

  onToggleExpanded({id, expanded}, keyword): void {
    this.keywordActions.toggleKeywordExpanded(keyword, expanded)
  }

  onToggleEditing({id, editing}, keyword): void {
    this.keywordActions.toggleKeywordEditing(keyword, editing)
  }
}
